import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ContentContainer,
  DefaultButton,
  Dropdown,
  Expander,
  FieldSet,
  HeaderTitle,
  ICON_PROPS,
  MainContentContainer,
  ObjectList,
  Panel,
  PrimaryButton,
  TextField,
} from "@riag-libs/etax-pattern-library";
import {
  ChoiceGroup,
  Dialog,
  DialogFooter,
  DialogType,
  Link,
  MessageBar,
  MessageBarType,
  PanelType,
  Spinner,
  SpinnerSize,
  Text,
} from "@fluentui/react";
import HttpService from "../../services/HttpService";
import QRCode from "qrcode";
import { KantonContext } from "../../contexts";
import { SELECTABLE_TAX_YEARS } from "../../constants/constants";

const EXPIRATION_OPTIONS = [
  { key: "30min", text: "30 Minuten" },
  {
    key: "12months",
    text: "12 Monate (kann in SNAP.SHARE mobile App gespeichert werden)",
  },
];

const TAX_YEAR_OPTIONS = SELECTABLE_TAX_YEARS.map((year) => ({
  key: year.toString(),
  text: year.toString(),
}));

const calculateExpiresAt = (duration) => {
  const now = new Date();
  if (duration === "30min") {
    return new Date(now.getTime() + 30 * 60 * 1000).toISOString();
  }
  const future = new Date(now);
  future.setFullYear(future.getFullYear() + 1);
  return future.toISOString();
};

const formatExpiryDate = (expiresAt) => {
  const date = new Date(expiresAt);
  return `${date.toLocaleDateString("de-CH")} ${date.toLocaleTimeString(
    "de-CH",
    { hour: "2-digit", minute: "2-digit" }
  )}`;
};

const formatValidityText = (qrCode) => {
  const dateStr = formatExpiryDate(qrCode.expiresAt);
  const parts = [];
  if (qrCode.taxYear) {
    parts.push(`Steuerjahr ${qrCode.taxYear}`);
  }
  if (qrCode.description) {
    parts.push(qrCode.description);
  }
  return parts.length
    ? `Gültig bis ${dateStr} (${parts.join(" - ")})`
    : `Gültig bis ${dateStr}`;
};

export const SnapsharePanel = ({
  mandant,
  isOpen,
  onDismiss,
  onDocumentsFetched,
}) => {
  const kantonInfo = useContext(KantonContext);
  const allowLongQRCode = kantonInfo.snapshareLongQRCodes === true;

  const [expirationDuration, setExpirationDuration] = useState("30min");
  const [taxYear, setTaxYear] = useState(TAX_YEAR_OPTIONS[0].key);
  const [description, setDescription] = useState("");
  const [activeQRCodes, setActiveQRCodes] = useState([]);

  const [isLoadingQRCodes, setIsLoadingQRCodes] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isQROverlayOpen, setIsQROverlayOpen] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [qrCodeToDelete, setQrCodeToDelete] = useState(null);

  const canvasRef = useRef(null);

  const fetchActiveQRCodes = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setIsLoadingQRCodes(true);
      try {
        const response = await HttpService.getAxiosClient().get(
          `/api/documents/snapshare/list/${mandant.SubjektId}`
        );
        if (response.status === 200) {
          const sorted = (response.data || []).sort(
            (a, b) => new Date(a.expiresAt) - new Date(b.expiresAt)
          );
          setActiveQRCodes(sorted);
        }
      } catch (error) {
        console.error("Error fetching active QR codes:", error);
      } finally {
        if (!silent) setIsLoadingQRCodes(false);
      }
    },
    [mandant.SubjektId]
  );

  useEffect(() => {
    if (isOpen) {
      fetchActiveQRCodes();
    }
  }, [isOpen, fetchActiveQRCodes]);

  useEffect(() => {
    if (!selectedQRCode || !isQROverlayOpen) return;

    const generateQRCode = async () => {
      if (!canvasRef.current || !document.body.contains(canvasRef.current)) {
        setTimeout(generateQRCode, 100);
        return;
      }
      const data = JSON.stringify({
        appid: selectedQRCode.appid,
        id: selectedQRCode.requestId,
        expiresAt: selectedQRCode.expiresAt,
        serverId: selectedQRCode.serverId,
      });
      try {
        await QRCode.toCanvas(canvasRef.current, data, {
          errorCorrectionLevel: "H",
          width: 200,
          margin: 0,
        });
      } catch (err) {
        console.error("Error generating QR code:", err);
      }
    };
    setTimeout(generateQRCode, 300);
  }, [selectedQRCode, isQROverlayOpen]);

  const handleCreateQRCode = async () => {
    setIsCreating(true);
    try {
      const duration = allowLongQRCode ? expirationDuration : "30min";
      const expiresAt = calculateExpiresAt(duration);
      const createResponse = await HttpService.getAxiosClient().post(
        "/api/documents/snapshare/create",
        {
          SubjektId: parseInt(mandant.SubjektId),
          description: description || "Snapshare",
          fileName: "Snapshare.pdf",
          mimeType: "application/pdf",
          expiresAt: expiresAt,
          properties: {
            taxYear: parseInt(taxYear),
            uploadedAt: new Date().toISOString(),
            expiresAt: expiresAt,
          },
        }
      );
      if (createResponse?.data) {
        const qrData = {
          ...createResponse.data,
          requestId: createResponse.data.id,
          expiresAt: createResponse.data.expiresAt || expiresAt,
          description: description,
        };
        setSelectedQRCode(qrData);
        setIsQROverlayOpen(true);
        setDescription("");
      }
    } catch (error) {
      console.error("Error creating QR code:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleShowQRCode = (qrCode) => {
    setSelectedQRCode(qrCode);
    setIsQROverlayOpen(true);
  };

  const handleCloseQROverlay = () => {
    setIsQROverlayOpen(false);
    setSelectedQRCode(null);
    fetchActiveQRCodes({ silent: true });
    onDocumentsFetched?.();
  };

  const handleDeleteQRCode = (qrCode) => {
    setQrCodeToDelete(qrCode);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteQRCode = async () => {
    if (!qrCodeToDelete) return;
    const deletingId = qrCodeToDelete.requestId;
    setActiveQRCodes((prev) => prev.filter((qr) => qr.requestId !== deletingId));
    setIsDeleteDialogOpen(false);
    setQrCodeToDelete(null);
    try {
      await HttpService.getAxiosClient().delete(
        `/api/documents/snapshare/delete/${deletingId}`
      );
    } catch (error) {
      console.error("Error deleting QR code:", error);
      fetchActiveQRCodes();
    }
  };

  const onRenderFooterContent = React.useCallback(
    () => <DefaultButton onClick={onDismiss}>Schliessen</DefaultButton>,
    [onDismiss]
  );

  return (
    <>
      <Panel
        headerText={
          <HeaderTitle
            title="SNAP.SHARE"
            subTitle={`${mandant.name} (PID: ${mandant.SubjektId})`}
            logo={{ iconName: "QRCode" }}
            className="l-m-b-16"
          />
        }
        isOpen={isOpen}
        dismissPanel={() => {
          if (!isQROverlayOpen && !isDeleteDialogOpen) {
            onDismiss();
          }
        }}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}
        panelType={PanelType.medium}
        className="owTheme pivotPanel"
      >
        <MainContentContainer>
          <ContentContainer className="vertical">
            <FieldSet
              title="Neuer QR-Code erstellen"
              headingTag="h2"
              headingTagClass="title2 l-m-tb-16"
            >
              {allowLongQRCode && (
                <ChoiceGroup
                  label="Gültigkeitsdauer des QR-Codes"
                  options={EXPIRATION_OPTIONS}
                  selectedKey={expirationDuration}
                  onChange={(e, option) =>
                    setExpirationDuration(option?.key || "30min")
                  }
                />
              )}
              <Dropdown
                label="Steuerjahr"
                options={TAX_YEAR_OPTIONS}
                selectedKey={taxYear}
                onChange={(e, option) =>
                  setTaxYear(option?.key || TAX_YEAR_OPTIONS[0].key)
                }
              />
              <TextField
                label="Bezeichnung (optional)"
                value={description}
                maxLength={64}
                onChange={(e, newValue) => setDescription(newValue || "")}
                description={`${description.length}/64`}
              />
              <ContentContainer className="l-m-t-8">
                <PrimaryButton
                  text="Neuer QR-Code anzeigen"
                  iconProps={{ iconName: "QRCode" }}
                  onClick={handleCreateQRCode}
                  disabled={isCreating}
                />
              </ContentContainer>
            </FieldSet>

            {(isLoadingQRCodes || activeQRCodes.length > 0) && (
              <ObjectList
                title="Aktive SNAP.SHARE QR-Codes"
                headingTag="h2"
                headingTagClass="title2"
              >
                {isLoadingQRCodes ? (
                  <Text
                    style={{
                      padding: "16px",
                      color: "var(--neutralSecondary)",
                    }}
                  >
                    Wird geladen...
                  </Text>
                ) : (
                  activeQRCodes.map((qrCode) => (
                    <Expander
                      key={qrCode.requestId}
                      title={formatValidityText(qrCode)}
                      headerTitleTag="h3"
                      headerTitleTagClass="title3"
                      className="formContent"
                    >
                      <ContentContainer className="vertical l-flex-rowgap-8">
                        <ContentContainer>
                          <DefaultButton
                            text="QR-Code anzeigen"
                            iconProps={{ iconName: "View" }}
                            onClick={() => handleShowQRCode(qrCode)}
                          />
                          <DefaultButton
                            text="QR-Code löschen"
                            iconProps={ICON_PROPS.DeleteIcon}
                            onClick={() => handleDeleteQRCode(qrCode)}
                          />
                        </ContentContainer>
                      </ContentContainer>
                    </Expander>
                  ))
                )}
              </ObjectList>
            )}
          </ContentContainer>
        </MainContentContainer>
      </Panel>

      <Dialog
        hidden={!isQROverlayOpen}
        onDismiss={handleCloseQROverlay}
        dialogContentProps={{
          type: DialogType.normal,
          title: "SNAP.SHARE",
        }}
        minWidth={400}
      >
        <ContentContainer
          className="vertical l-flex-align-center l-flex-justify-center"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {selectedQRCode ? (
            new Date(selectedQRCode.expiresAt) < new Date() ? (
              <MessageBar messageBarType={MessageBarType.warning}>
                QR-Code ist nicht mehr gültig. Bitte erstellen Sie einen neuen
                QR-Code.
              </MessageBar>
            ) : (
              <div
                style={{
                  flexDirection: "column",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                {new Date(selectedQRCode.expiresAt) < new Date() ? (
                  <Text variant="medium">
                    Dieser QR-Code ist nicht mehr gültig. Bitte erstellen Sie
                    einen neuen QR-Code.
                  </Text>
                ) : (
                  <>
                    <Text variant="medium">
                      Scannen Sie den QR-Code mit der SNAP.SHARE Mobile App und
                      laden Sie Ihre Belege zur Steuererklärung hoch. Anleitung
                      und App-Download unter{" "}
                      <Link
                        href={"https://snapshare.ch/"}
                        target={"_blank"}
                        underline
                      >
                        snapshare.ch
                      </Link>
                    </Text>
                    <div
                      style={{
                        padding: "12px",
                        border: "1px solid lightgray",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <canvas
                        ref={canvasRef}
                        width={200}
                        height={200}
                        style={{
                          marginBottom: "0",
                          marginTop: "0",
                          display: "block",
                        }}
                      />
                    </div>
                  </>
                )}
                <Text
                  variant="medium"
                  style={{
                    color: "var(--neutralSecondary)",
                    textAlign: "center",
                  }}
                >
                  {formatValidityText(selectedQRCode)}
                </Text>
              </div>
            )
          ) : (
            <Spinner size={SpinnerSize.medium} />
          )}
        </ContentContainer>
        <DialogFooter>
          <DefaultButton text="Schliessen" onClick={handleCloseQROverlay} />
        </DialogFooter>
      </Dialog>

      <Dialog
        hidden={!isDeleteDialogOpen}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setQrCodeToDelete(null);
        }}
        dialogContentProps={{
          type: DialogType.normal,
          title: "QR-Code wirklich löschen?",
          subText:
            "Das Löschen des QR-Codes macht ihn ungültig. Auf der mobilen App wird eine Fehlermeldung angezeigt, wenn der Code erneut verwendet wird.",
        }}
        minWidth={400}
      >
        <DialogFooter>
          <PrimaryButton text="Löschen" onClick={confirmDeleteQRCode} />
          <DefaultButton
            text="Abbrechen"
            onClick={() => {
              setIsDeleteDialogOpen(false);
              setQrCodeToDelete(null);
            }}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};
